import type { CreateUpdateDriver, Driver } from '../types/Driver';
import { api } from './api';
import type { PaginatedListResponse } from './response_models/PaginatedListIResponse';
import type { GetDriversStatsResponse } from './response_models/drivers/GetDriversStatsResponse';

interface GetDriversRequest {
  page?: number;
  pageSize?: number;
  searchText?: string;
  id?: string;
  risk_factor_min?: number;
  risk_factor_max?: number;
}

interface GetDriverReferenceImagesRequest {
  driver_id: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: string;
}

export const driversApi = api.injectEndpoints({
  endpoints: builder => ({
    getDrivers: builder.query<PaginatedListResponse<Driver>, GetDriversRequest | void>({
      query: args => {
        let queryString = `drivers/`;

        if (args && Object.keys(args).length !== 0) {
          queryString += `?page=${args.page}&page_size=${args.pageSize}&search=${args.searchText}`;
        }
        if (
          args?.risk_factor_min !== undefined &&
          args?.risk_factor_min !== null &&
          args?.risk_factor_max !== undefined &&
          args?.risk_factor_max !== null
        ) {
          queryString += `&risk_factor_min=${args.risk_factor_min}&risk_factor_max=${args.risk_factor_max}`;
        }
        return queryString;
      },
    }),
    getAllDrivers: builder.query<PaginatedListResponse<Driver>, void>({
      query: () => 'drivers/',
    }),
    getDriverImage: builder.query<Driver, GetDriversRequest>({
      query: args => {
        if (args && args.id) {
          return `drivers/${args.id}/`;
        }
        return `drivers/`;
      },
    }),
    getDriversStats: builder.query<GetDriversStatsResponse, void>({
      query: () => 'drivers/stats/',
    }),
    createDriver: builder.mutation<Driver, Partial<CreateUpdateDriver>>({
      query: driver => ({
        url: `drivers/`,
        method: 'POST',
        body: driver,
      }),
    }),
    updateProfilePicture: builder.mutation<void, FormData>({
      query: formData => ({
        url: `drivers/upload_profile_picture/`,
        method: 'POST',
        body: formData,
      }),
    }),
    updateDriver: builder.mutation<Driver, { id: string; driver: Partial<CreateUpdateDriver> }>({
      query: ({ id, driver }) => ({
        url: `drivers/${id}/`,
        method: 'PATCH',
        body: driver,
      }),
    }),
    deleteDriver: builder.mutation<void, string>({
      query: id => ({
        url: `drivers/${id}/`,
        method: 'DELETE',
      }),
    }),
    createReferenceImages: builder.mutation<{ image_path: string }[], { driver: string; filenames: string[] }>({
      query: data => ({
        url: 'bulk_create_reference_images/',
        method: 'POST',
        body: data.filenames.map(filename => ({
          driver: data.driver,
          filename,
        })),
      }),
    }),
    getSignedUploadUrl: builder.mutation<{ url: string }, { storage_path: string }>({
      query: data => ({
        url: `generate_presigned_upload_url/?storage_path=${data.storage_path}`,
        method: 'GET',
      }),
    }),
    getDriverReferenceImages: builder.query<
      PaginatedListResponse<{ id: string; image_path: string }>,
      GetDriverReferenceImagesRequest
    >({
      query: data => ({
        url: `get_reference_images/?driver_id=${data.driver_id}&page=${data.page}&page_size=${data.pageSize}`,
        method: 'GET',
      }),
    }),
    updateDriverReferenceImage: builder.mutation<{ image_path: string }, { id: string }>({
      query: ({ id }) => ({
        url: `update_reference_images/${id}/`,
        method: 'PATCH',
      }),
    }),
    deleteDriverReferenceImage: builder.mutation<void, string>({
      query: id => ({
        url: `delete_reference_images/${id}/`,
        method: 'DELETE',
      }),
    }),
    listAllDrivers: builder.query<Partial<Driver[]>, void>({
      query: () => 'list_drivers/',
    }),
  }),
});

export const {
  useGetAllDriversQuery,
  useLazyGetDriverImageQuery,
  useLazyGetDriversStatsQuery,
  useLazyGetDriversQuery,
  useCreateDriverMutation,
  useUpdateDriverMutation,
  useDeleteDriverMutation,
  useUpdateProfilePictureMutation,
  useCreateReferenceImagesMutation,
  useGetSignedUploadUrlMutation,
  useGetDriverReferenceImagesQuery,
  useLazyGetDriverReferenceImagesQuery,
  useUpdateDriverReferenceImageMutation,
  useDeleteDriverReferenceImageMutation,
  useLazyListAllDriversQuery,
} = driversApi;
